import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  ChevronRight,
  Inbox as InboxIcon,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { listMyLeads, updateLeadStatus } from '../services/leads';
import { formatMoney } from '../services/valuation';
import type { Lead, LeadStatus } from '../types';

interface InboxProps {
  shareUrl: string | null;
  onGoToWidget: () => void;
}

const STATUS_OPTIONS: LeadStatus[] = [
  'new',
  'contacted',
  'booked',
  'instructed',
  'dead',
];

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  booked: 'Booked',
  instructed: 'Instructed',
  dead: 'Dead',
};

const STATUS_STYLES: Record<LeadStatus, { bg: string; color: string; border: string }> = {
  new: {
    bg: 'rgba(244, 122, 107, 0.14)',
    color: '#f9a397',
    border: 'rgba(244, 122, 107, 0.3)',
  },
  contacted: {
    bg: 'rgba(234, 179, 8, 0.14)',
    color: '#fcd34d',
    border: 'rgba(234, 179, 8, 0.3)',
  },
  booked: {
    bg: 'rgba(59, 130, 246, 0.14)',
    color: '#93c5fd',
    border: 'rgba(59, 130, 246, 0.3)',
  },
  instructed: {
    bg: 'rgba(16, 185, 129, 0.14)',
    color: '#6ee7b7',
    border: 'rgba(16, 185, 129, 0.3)',
  },
  dead: {
    bg: 'rgba(148, 163, 184, 0.12)',
    color: '#94a3b8',
    border: 'rgba(148, 163, 184, 0.25)',
  },
};

type Filter = 'open' | LeadStatus | 'all';

const FILTERS: { id: Filter; label: string; help?: string }[] = [
  { id: 'open', label: 'Open', help: 'New + Contacted + Booked' },
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'booked', label: 'Booked' },
  { id: 'instructed', label: 'Instructed' },
  { id: 'dead', label: 'Dead' },
  { id: 'all', label: 'All' },
];

export default function Inbox({ shareUrl, onGoToWidget }: InboxProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('open');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listMyLeads().then((rows) => {
      if (cancelled) return;
      setLeads(rows);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => {
    const acc: Record<LeadStatus, number> = {
      new: 0,
      contacted: 0,
      booked: 0,
      instructed: 0,
      dead: 0,
    };
    for (const l of leads) acc[l.status] = (acc[l.status] ?? 0) + 1;
    return acc;
  }, [leads]);

  const filtered = useMemo(() => {
    if (filter === 'all') return leads;
    if (filter === 'open') {
      return leads.filter(
        (l) => l.status === 'new' || l.status === 'contacted' || l.status === 'booked'
      );
    }
    return leads.filter((l) => l.status === filter);
  }, [leads, filter]);

  const onChangeStatus = async (leadId: string, status: LeadStatus) => {
    setUpdatingId(leadId);
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, status } : l))
    );
    const r = await updateLeadStatus(leadId, status);
    setUpdatingId(null);
    if (!r.success) {
      // Revert on failure.
      setLeads(await listMyLeads());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-8">
        <Loader2 size={16} className="animate-spin" />
        Loading your leads…
      </div>
    );
  }

  if (leads.length === 0) {
    return <EmptyInbox shareUrl={shareUrl} onGoToWidget={onGoToWidget} />;
  }

  const filterCount = (id: Filter): number | undefined => {
    if (id === 'all') return leads.length;
    if (id === 'open') return counts.new + counts.contacted + counts.booked;
    return counts[id];
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-1 mb-4">
        {FILTERS.map((f) => {
          const n = filterCount(f.id);
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              title={f.help}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                active
                  ? 'bg-slate-800 text-white border border-slate-700'
                  : 'text-slate-400 hover:text-slate-200 border border-transparent'
              }`}
            >
              {f.label}
              {n !== undefined && n > 0 && (
                <span
                  className={`text-[10px] font-semibold px-1.5 rounded-full ${
                    active ? 'bg-slate-700 text-slate-200' : 'bg-slate-900 text-slate-500'
                  }`}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-slate-500 py-12 text-center">
          No leads match this filter.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((lead) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              expanded={expandedId === lead.id}
              updating={updatingId === lead.id}
              onToggle={() =>
                setExpandedId(expandedId === lead.id ? null : lead.id)
              }
              onStatusChange={(s) => onChangeStatus(lead.id, s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LeadRow({
  lead,
  expanded,
  updating,
  onToggle,
  onStatusChange,
}: {
  lead: Lead;
  expanded: boolean;
  updating: boolean;
  onToggle: () => void;
  onStatusChange: (s: LeadStatus) => void;
}) {
  const styles = STATUS_STYLES[lead.status];

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-900/40 transition-colors"
      >
        <div className="text-slate-600 shrink-0">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm truncate">
              {lead.contact_name}
            </span>
            <StatusPill status={lead.status} />
          </div>
          <div className="text-xs text-slate-500 truncate mt-0.5">
            {lead.address_snapshot ?? lead.postcode ?? 'No address on lead'}
          </div>
        </div>

        <div className="hidden sm:flex flex-col items-end shrink-0">
          {lead.estimate_low && lead.estimate_high ? (
            <div className="text-xs text-slate-300 font-semibold">
              {formatMoney(lead.estimate_low)} – {formatMoney(lead.estimate_high)}
            </div>
          ) : (
            <div className="text-[11px] text-slate-600">No estimate shown</div>
          )}
          <div className="text-[11px] text-slate-500 mt-0.5">{relativeTime(lead.created_at)}</div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-900 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          {/* Contact */}
          <div className="space-y-2 pt-3">
            <SectionLabel>Contact</SectionLabel>
            {lead.contact_phone && (
              <a
                href={`tel:${lead.contact_phone.replace(/\s+/g, '')}`}
                className="flex items-center gap-1.5 text-slate-300 hover:text-white"
              >
                <Phone size={12} className="text-slate-500" />
                {lead.contact_phone}
              </a>
            )}
            {lead.contact_email && (
              <a
                href={`mailto:${lead.contact_email}`}
                className="flex items-center gap-1.5 text-slate-300 hover:text-white break-all"
              >
                <Mail size={12} className="text-slate-500 shrink-0" />
                {lead.contact_email}
              </a>
            )}
            {lead.best_time_to_call && (
              <div className="text-slate-400">
                <span className="text-slate-500">Best time:</span>{' '}
                {lead.best_time_to_call}
              </div>
            )}
            {lead.notes && (
              <div className="text-slate-400 whitespace-pre-wrap">
                <div className="text-slate-500 mb-0.5">Notes</div>
                {lead.notes}
              </div>
            )}
          </div>

          {/* Property + valuation */}
          <div className="space-y-2 pt-3">
            <SectionLabel>Property</SectionLabel>
            {lead.address_snapshot && (
              <div className="flex items-start gap-1.5 text-slate-300">
                <MapPin size={12} className="text-slate-500 mt-0.5" />
                <span className="break-words">{lead.address_snapshot}</span>
              </div>
            )}
            {lead.postcode && !lead.address_snapshot && (
              <div className="text-slate-300">{lead.postcode}</div>
            )}
            {lead.estimate_low && lead.estimate_high && (
              <div className="space-y-0.5">
                <div className="text-slate-500">Valuation shown</div>
                <div className="text-slate-200 font-semibold">
                  {formatMoney(lead.estimate_low)} –{' '}
                  {formatMoney(lead.estimate_high)}
                </div>
                {lead.confidence && (
                  <div className="text-[11px] text-slate-500 capitalize">
                    {lead.confidence} confidence
                  </div>
                )}
              </div>
            )}
            <div className="text-[11px] text-slate-600 mt-2">
              Source: {lead.source.replace('_', ' ')} · {formatDate(lead.created_at)}
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2 pt-3">
            <SectionLabel>Pipeline stage</SectionLabel>
            <div className="grid grid-cols-2 gap-1.5">
              {STATUS_OPTIONS.map((s) => {
                const active = lead.status === s;
                const style = STATUS_STYLES[s];
                return (
                  <button
                    key={s}
                    onClick={() => onStatusChange(s)}
                    disabled={updating}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
                      active ? '' : 'border-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                    style={
                      active
                        ? {
                            background: style.bg,
                            color: style.color,
                            borderColor: style.border,
                          }
                        : undefined
                    }
                  >
                    {STATUS_LABELS[s]}
                  </button>
                );
              })}
            </div>
            {updating && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-1">
                <Loader2 size={11} className="animate-spin" />
                Updating…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: LeadStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border"
      style={{ background: s.bg, color: s.color, borderColor: s.border }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
      {children}
    </div>
  );
}

function EmptyInbox({
  shareUrl,
  onGoToWidget,
}: {
  shareUrl: string | null;
  onGoToWidget: () => void;
}) {
  return (
    <div className="p-8 rounded-2xl border border-slate-800 bg-slate-900/40 text-center">
      <div
        className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center"
        style={{ background: 'rgba(244, 122, 107, 0.12)', color: '#f47a6b' }}
      >
        <InboxIcon size={22} />
      </div>
      <h3 className="text-white font-semibold text-base mb-1">
        No valuation leads yet
      </h3>
      <p className="text-sm text-slate-400 max-w-md mx-auto leading-relaxed mb-5">
        When a homeowner submits a valuation through your widget, they'll
        appear here. Share your URL to start collecting leads.
      </p>
      {shareUrl ? (
        <div className="space-y-2">
          <div className="px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 font-mono text-xs text-white break-all max-w-md mx-auto">
            {shareUrl}
          </div>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold transition-colors"
            style={{ color: '#f47a6b' }}
          >
            <Sparkles size={12} />
            Preview your widget
            <ExternalLink size={11} />
          </a>
        </div>
      ) : (
        <button
          onClick={onGoToWidget}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white font-semibold text-sm transition-colors"
          style={{ background: '#f47a6b' }}
        >
          Set up your widget first
        </button>
      )}
    </div>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms) || ms < 0) return formatDate(iso);
  const m = ms / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)}m ago`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)}h ago`;
  const d = h / 24;
  if (d < 7) return `${Math.floor(d)}d ago`;
  return formatDate(iso);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
